<template>
  <EquivalentExtractionCopyBase
    :subtitle="'Browse finalized medical alternatives from equivalent reviews.'"
    :cache-key="study.id"
    :fetch-studies="fetchStudies"
    @copy="emit('copyMedicalAlternatives', $event)"
  >
    <template #preview="{ extraction }">
      <div v-if="extraction.value" class="mt-2 space-y-3 overflow-y-auto pb-2">
        <div
          v-for="(alt, i) in extraction.value.alternatives"
          :key="i"
          class="border border-gray-200 rounded-lg bg-white overflow-hidden"
        >
          <div
            class="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
            @click="toggleExpand(i)"
          >
            <div class="pr-4">
              <p class="text-sm font-medium text-gray-900">
                {{ alt.name || `Alternative ${i + 1}` }}
              </p>
              <p
                v-if="alt.description && expandedIndex !== i"
                class="text-xs text-gray-500 mt-0.5 line-clamp-1"
              >
                {{ alt.description }}
              </p>
            </div>
            <i-material-symbols-keyboard-arrow-down-rounded
              class="w-5 h-5 text-gray-500 shrink-0 transition-transform duration-200"
              :class="{ 'rotate-180': expandedIndex === i }"
            />
          </div>

          <div
            v-show="expandedIndex === i"
            class="p-3 pt-0 border-t border-gray-100 bg-gray-50/50 space-y-2"
          >
            <p v-if="alt.description" class="text-xs text-gray-500 mt-2">
              {{ alt.description }}
            </p>

            <div v-if="alt.indications?.length" class="flex flex-col gap-1 mt-2">
              <span class="text-[10px] font-semibold text-gray-700 uppercase tracking-wide"
                >Indications</span
              >
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="ind in alt.indications"
                  :key="ind"
                  class="text-xs px-2.5 py-1.5 inline-block rounded-md bg-green-100 text-green-800 font-medium leading-relaxed"
                >
                  {{ ind }}
                </span>
              </div>
            </div>

            <div v-if="alt.contraindications?.length" class="flex flex-col gap-1 mt-2">
              <span class="text-[10px] font-semibold text-gray-700 uppercase tracking-wide"
                >Contraindications</span
              >
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="contra in alt.contraindications"
                  :key="contra"
                  class="text-xs px-2.5 py-1.5 inline-block rounded-md bg-red-100 text-red-800 font-medium leading-relaxed"
                >
                  {{ contra }}
                </span>
              </div>
            </div>

            <div v-if="alt.targetPopulation?.length" class="flex flex-col gap-1 mt-2">
              <span class="text-[10px] font-semibold text-gray-700 uppercase tracking-wide"
                >Target Population</span
              >
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="pop in alt.targetPopulation"
                  :key="pop"
                  class="text-xs px-2.5 py-1.5 inline-block rounded-md bg-blue-100 text-blue-800 font-medium leading-relaxed"
                >
                  {{ pop }}
                </span>
              </div>
            </div>

            <div
              v-if="hasAssessment(alt)"
              class="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3"
            >
              <div v-if="alt.assessment?.positionInTherapy">
                <span class="text-[10px] font-semibold text-gray-500 block"
                  >Position in Therapy</span
                >
                <span class="text-xs text-gray-900">{{
                  formatEnumLabel(alt.assessment.positionInTherapy)
                }}</span>
              </div>
              <div v-if="alt.assessment?.benchmarkAuthority">
                <span class="text-[10px] font-semibold text-gray-500 block"
                  >Benchmark Authority</span
                >
                <span class="text-xs text-gray-900">{{
                  formatEnumLabel(alt.assessment.benchmarkAuthority)
                }}</span>
              </div>
              <div v-if="alt.assessment?.modeOfUse">
                <span class="text-[10px] font-semibold text-gray-500 block">Mode of Use</span>
                <span class="text-xs text-gray-900">{{
                  formatEnumLabel(alt.assessment.modeOfUse)
                }}</span>
              </div>
              <div v-if="alt.assessment?.innovationStatus">
                <span class="text-[10px] font-semibold text-gray-500 block">Innovation Status</span>
                <span class="text-xs text-gray-900">{{
                  formatEnumLabel(alt.assessment.innovationStatus)
                }}</span>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="extraction.value.equipoise"
          class="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-md border border-blue-100"
        >
          <i-material-symbols-info-outline class="w-4 h-4" />
          <p class="text-xs font-medium">Equipoise: Alternatives are considered equivalent</p>
        </div>
      </div>
      <div v-else class="text-sm text-gray-500 italic">No value extracted</div>
    </template>
  </EquivalentExtractionCopyBase>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { injectStrict } from "@app/utils/injectStrict";
import { EvidenceServiceApiKey } from "@app/injectionKeys";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import type {
  MedicalAlternativesValue,
  MedicalAlternative,
} from "@core/domain/types/attributeValue.type";
import EquivalentExtractionCopyBase from "./EquivalentExtractionCopyBase.vue";
import type { EquivalentStudyEntry } from "./EquivalentExtractionCopyBase.vue";

const expandedIndex = ref<number | null>(null);

function toggleExpand(index: number) {
  expandedIndex.value = expandedIndex.value === index ? null : index;
}

const props = defineProps<{
  study: ReviewItem;
}>();

const emit = defineEmits<{
  copyMedicalAlternatives: [value: MedicalAlternativesValue];
}>();

const evidenceService = injectStrict(EvidenceServiceApiKey);

async function fetchStudies(): Promise<EquivalentStudyEntry<MedicalAlternativesValue>[]> {
  const response =
    await evidenceService.api.reviewItemsControllerGetEquivalentFinalizedMedicalAlternatives(
      props.study.id,
    );
  return response.data.map((s) => ({
    reviewSeriesName: s.reviewSeriesName,
    study: {
      id: s.id,
      title: s.title,
      extractions: s.extractions.map((e) => ({
        label: e.label,
        attributeStructureId: e.attributeStructureId,
        value: e.value as MedicalAlternativesValue,
      })),
    },
  }));
}

function formatEnumLabel(value: string): string {
  const formatted = value.replace(/-/g, " ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

function hasAssessment(alt: MedicalAlternative): boolean {
  const a = alt.assessment;
  return !!(a?.positionInTherapy || a?.benchmarkAuthority || a?.modeOfUse || a?.innovationStatus);
}
</script>
